import {
  CatalogCellType,
  ColSourceGroup,
  ColumnCatalogEntry,
  HELIOS_GROUP,
} from '../models/source-definition.model'
import {
  ColumnDescriptor,
  ColumnGroupInfo,
  ColumnGroupKey,
  ColumnStorage,
  ColumnType,
  SourceFieldCatalog,
} from '../models/field-catalog.model'

/**
 * Pure adapter mapping the Flex Positions field-catalog API shape onto the internal
 * {@link ColumnCatalogEntry} shape the ColDef factory already consumes. This is the ONLY seam
 * between the API and the tested rendering logic in col-def-factory.ts — that file (and its
 * `catalogColDef`) stay unchanged.
 *
 * The API differs from the internal shape on four axes, each concentrated here:
 *  - type (value semantics) vs cellType (rendering hint): TEXT/DATE/DATETIME/BOOL map straight;
 *    NUM maps to `number` and is upgraded to money at RUNTIME by the value-shape-driven money
 *    formatter (see renderers.isMoney) — the API has no MONEY type.
 *  - colSource group key vs the internal display-label union: resolved via the server-provided
 *    ColumnGroupInfo.label, preserving the server's top-to-bottom group order.
 *  - visibleByDefault vs hide: hide = !visibleByDefault.
 *  - the API returns no width: a type-driven default is supplied here.
 */

/** Default column width per value type — the API returns none, so the UI owns this. */
export const WIDTH_BY_TYPE: Record<ColumnType, number> = {
  TEXT: 140,
  NUM: 120,
  DATE: 120,
  DATETIME: 170,
  TIME: 120,
  BOOL: 100,
}

/**
 * Resolve a descriptor's physical storage.
 *
 * The contract always sends `storage`, and it is explicitly NOT derivable from `colSource` — so
 * there is no general inference to fall back on. This exists only so a catalog served by a backend
 * predating PVAL-26116 degrades instead of blanking the grid: the ONE safe special case is that an
 * AGGREGATE_INFO-sourced column is agg-stored (the group exists for exactly those keys), and
 * everything else lands on `COLUMN`, which is the type-trustworthy branch — i.e. the pre-PVAL-26116
 * behaviour. A `POS_DATA` or `MATCH_COUNTS` column then loses nothing but the aggInfo hint it never
 * needed.
 */
const storageOf = (descriptor: ColumnDescriptor): ColumnStorage => {
  if (descriptor.storage) return descriptor.storage
  return descriptor.colSource === 'AGGREGATE_INFO' ? 'AGG_INFO' : 'COLUMN'
}

/**
 * Per-field width overrides for the handful of columns whose ideal width the type default misses.
 * Keyed on the canonical `field`.
 */
export const WIDTH_BY_FIELD: Record<string, number> = {}

/** Map an API value type to the internal cell-rendering type. NUM stays `number`; money is a
 * runtime value-shape upgrade, never type-driven (contract Decision 5). */
const cellTypeOf = (type: ColumnType): CatalogCellType => {
  switch (type) {
    case 'DATE':
      return 'date'
    case 'DATETIME':
      return 'datetime'
    case 'BOOL':
      return 'boolean'
    case 'NUM':
      return 'number'
    // TIME has no dedicated renderer — the API sends it as a display-ready string, so it renders as
    // text. Its `valueType` still travels as TIME, which is what /query needs for `filterType: time`
    // / `type: TIME`; collapsing it to TEXT here would make a time column sort lexically.
    case 'TIME':
    case 'TEXT':
    default:
      return 'text'
  }
}

/**
 * Fallback key→internal-group-label map, used only when the server omits a group's label. The
 * server-provided ColumnGroupInfo.label is authoritative when present.
 *
 * Exported for the Position Detail sub-panel, which resolves the row-detail API's `columnGroup`
 * against the row's own catalog first and falls back to this same map — so both surfaces label a
 * group identically.
 */
export const GROUP_LABEL_BY_KEY: Record<ColumnGroupKey, ColSourceGroup> = {
  HELIOS: 'Helios',
  DEFAULT: 'Default',
  POSITION: 'Position',
  CASH_BALANCE: 'Cash Balance',
  SECURITY_MASTER: 'Security Master',
  EXTERNAL: 'External',
  ELM: 'ELM',
  AGGREGATE_INFO: 'Aggregate Info',
}

/**
 * Title-case an unrecognised group token so an unexpected key still renders a legible label rather
 * than being dropped: `SOME_NEW_GROUP` -> `Some New Group`, `someNewGroup` -> `Some New Group`.
 *
 * Lives here rather than beside its other caller (the Position Detail view model) because the grid
 * is the lower layer: the view model already imports {@link GROUP_LABEL_BY_KEY} from this file, so
 * sharing one implementation keeps the two label ladders from drifting apart.
 */
export const titleCaseGroupToken = (token: string): string =>
  token
    // Split on underscores and on camelCase / ACRONYMWord boundaries alike.
    .replace(/_+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

/**
 * Resolve a column's group label: the server's own label first, then the fallback map for a key it
 * declared no group for, and finally the key itself made readable.
 *
 * The last step matters. Returning `undefined` here sends the column to the Column Manager's
 * ungrouped bucket, so a group key the server added but did not list in `groups[]` would silently
 * pile up under "Other". Falling back to the key keeps the column filed under the source the API
 * actually gave it. Mirrors {@link resolveGroupLabel} in the Position Detail view model.
 */
const groupLabelOf = (
  colSource: ColumnGroupKey,
  labels: Record<string, string>,
): ColSourceGroup =>
  (labels[colSource] ??
    GROUP_LABEL_BY_KEY[colSource] ??
    titleCaseGroupToken(colSource)) as ColSourceGroup

/** Build a colSource key -> display label lookup from the catalog's groups. */
const groupLabelLookup = (
  groups: ColumnGroupInfo[],
): Record<string, string> => {
  const lookup: Record<string, string> = {}
  groups.forEach(group => {
    lookup[group.key] = group.label
  })
  return lookup
}

/**
 * Ordered group display labels for one catalog, used by the Column Manager to lay its sections out
 * top to bottom. The server's group order is authoritative and is never sorted.
 *
 * `Helios` is forced to the front: the UI's computed leading columns live in that group but are not
 * catalog entries, so a catalog whose groups omit HELIOS would otherwise drop the section entirely.
 * Filtering before prepending keeps the group from appearing twice when the real API does return
 * it. A declared group carrying zero columns (contract Decision 7) still appears here; the Column
 * Manager drops an empty section itself.
 */
export function groupOrderFromApi(catalog: SourceFieldCatalog): string[] {
  const serverOrder = catalog.groups.map(
    group => group.label || GROUP_LABEL_BY_KEY[group.key],
  )
  return [HELIOS_GROUP, ...serverOrder.filter(label => label !== HELIOS_GROUP)]
}

/** One catalog adapted for the grid: ordered columns plus the Column Manager's section order. */
export interface AdaptedCatalog {
  columns: ColumnCatalogEntry[]
  /** Group labels in server display order. */
  groupOrder: string[]
}

/** Adapt an API catalog into everything the grid needs from it. */
export function adaptCatalog(catalog: SourceFieldCatalog): AdaptedCatalog {
  return {
    columns: catalogFromApi(catalog),
    groupOrder: groupOrderFromApi(catalog),
  }
}

export function catalogFromApi(
  catalog: SourceFieldCatalog,
): ColumnCatalogEntry[] {
  const labels = groupLabelLookup(catalog.groups)
  return catalog.columns.map(descriptor => {
    const colSource = groupLabelOf(descriptor.colSource, labels)
    return {
      field: descriptor.field,
      // Duplicate labels are expected (isin/ISIN, cusip/CUSIP, ...) and are distinguishable ONLY by
      // colSource, so the grid header carries it too — not just the Column Manager.
      headerName: descriptor.label,
      headerTooltip: `${descriptor.label} · ${colSource}`,
      colSourceLabel: colSource,
      colSource,
      cellType: cellTypeOf(descriptor.type),
      valueType: descriptor.type,
      storage: storageOf(descriptor),
      hide: !descriptor.visibleByDefault,
      width: WIDTH_BY_FIELD[descriptor.field] ?? WIDTH_BY_TYPE[descriptor.type],
      allowedValues: descriptor.allowedValues,
    }
  })
}
